import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  DEFAULT_MOBILE_DATE_FORMAT,
  generateDatasets,
  getEndDate,
  DEFAULT_SOURCE_CURRENCY,
  getDate,
  formatParsedDate,
} from 'utils';

import { getRatesMatrix, getRatesGraphTimeLine } from 'selectors';

import { DynamicRatesChart } from 'components/DynamicRatesChart';

export const MbChart = ({
  terms,
  type,
  chartTitle,
  chartValues,
  chartType,
  isOpen,
}) => {
  const { currency, date: currentDate } = useParams();

  const [period, changePeriod] = useState(
    type === 'interbank' ? 'day' : 'week'
  );
  const [date, setDate] = useState({
    from: '',
    to: '',
    custom: {},
  });

  const [currentOption, setCurrentOption] = useState(
    currency?.toUpperCase() || DEFAULT_SOURCE_CURRENCY.toUpperCase()
  );

  const { locale, formatMessage } = useIntl();

  useEffect(() => {
    const newCurrency =
      currency?.toUpperCase() || DEFAULT_SOURCE_CURRENCY.toUpperCase();
    setCurrentOption(newCurrency);
  }, [currency]);

  const termsWithLocale = useMemo(() => {
    return terms.map(term => ({
      ...term,
      label: formatMessage({ id: `${term.value}.${[term.type]}` }),
    }));
  }, [terms]);

  const params = useMemo(
    () => ({
      sourceCurrency: currentOption,
      targetCurrency: 'UAH',
      period,
      date,
      locale,
      type,
      requestType: type,
      currentDate,
    }),
    [
      date.from,
      date.to,
      locale,
      currentOption,
      date.custom?.from,
      date.custom?.to,
      period,
      currentDate,
    ]
  );

  const ratesMatrix = useSelector(state => getRatesMatrix(state, params));
  const ratesGraphTimeLine = useSelector(state =>
    getRatesGraphTimeLine(state, params)
  );

  useEffect(() => {
    if (date.custom?.from || date.custom?.to || !ratesGraphTimeLine?.from)
      return;

    if (currentDate) {
      const { dateFrom, dateTo } = getDate(currentDate, period);

      setDate({
        ...date,
        from: dateFrom,
        to: dateTo,
      });
    }

    if (!currentDate) {
      const startDate = formatParsedDate(
        ratesGraphTimeLine.from.date,
        DEFAULT_MOBILE_DATE_FORMAT
      );
      const endDate = getEndDate();

      setDate({ from: startDate, to: endDate });
    }
  }, [currentDate, period, ratesGraphTimeLine?.from]);

  const datasets = generateDatasets(
    termsWithLocale,
    ratesMatrix,
    chartType || type
  );

  return (
    <DynamicRatesChart
      params={params}
      changePeriod={changePeriod}
      currentOption={currentOption}
      setCurrentOption={setCurrentOption}
      ratesGraphTimeLine={ratesGraphTimeLine?.list}
      datasets={datasets}
      date={date}
      setDate={setDate}
      type="line"
      chartTitle={chartTitle}
      chartValues={chartValues}
      page={type}
      isOpenChart={isOpen}
    />
  );
};

MbChart.propTypes = {
  terms: PropTypes.arrayOf(PropTypes.shape({})),
  type: PropTypes.string.isRequired,
  chartTitle: PropTypes.string.isRequired,
  chartValues: PropTypes.shape({}),
  chartType: PropTypes.string,
  isOpen: PropTypes.bool,
};
MbChart.defaultProps = {
  terms: [
    { type: 'sell', value: 'mb' },
    { type: 'buy', value: 'mb' },
    { type: 'nbu', value: 'nbu' },
  ],
  chartType: null,
  chartValues: {},
  isOpen: true,
};
